// ============================================================
//  Транспорт WebRTC DataChannel.
//
//  Наружу торчит одна функция openRtc(on) → { send, close } — ровно такой же
//  интерфейс, как у обёртки над WebSocket в net.js. Всё остальное (пинг,
//  переподключение, разбор кадров) живёт там и про транспорт не знает.
//
//  Зачем второй транспорт, если пинг он не уменьшает: WebSocket едет поверх
//  TCP, и потерянный пакет держит ВСЕ следующие кадры до повторной доставки
//  (head-of-line blocking) — на канале владельца с джиттером ±60 мс это видно
//  как рывок. Канал ordered:false / maxRetransmits:0 теряет устаревший снапшот
//  и едет дальше, а устаревший снапшот игре и не нужен: следующий приедет
//  через 33 мс. Плюс два транспорта — это диагностика: когда «лагает», можно
//  отличить сеть от нашего кода, переключившись.
// ============================================================

/** Сколько ждём сбора ICE-кандидатов, прежде чем отправить offer как есть. */
//
// Сигналинг у нас одноразовый (без trickle ICE), поэтому offer уходит после
// сбора кандидатов. Обычно это десятки миллисекунд, но при недоступном
// STUN/TURN сбор может не завершиться вовсе — и без потолка вход в игру
// повис бы навсегда. Собранного к этому моменту (host + что успело) хватает.
const GATHER_TIMEOUT = 3000;

/** Адрес HTTP-API. Зеркалит логику wsUrl() из net.js. */
function apiBase() {
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL;
  const host = location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'http://127.0.0.1:8080';
  return location.origin;
}

/**
 * ICE-серверы для пробивания NAT.
 *
 * Учётки временные (TTL час) и считаются сервером по секрету — постоянную пару
 * в бандл класть нельзя, её вытащат и будут гонять через нас чужой трафик
 * (см. server/src/turn.rs). Не ответил — идём на голом STUN: прямое соединение
 * соберётся у большинства, а у кого нет — тот увидит, что WebRTC не поднялся,
 * и останется на WebSocket.
 */
async function iceServers() {
  try {
    const r = await fetch(`${apiBase()}/api/turn`, { cache: 'no-store' });
    if (!r.ok) throw new Error(String(r.status));
    const j = await r.json();
    return (j.ice_servers || []).map((s) => ({
      urls: s.urls,
      ...(s.username ? { username: s.username, credential: s.credential } : {}),
    }));
  } catch {
    return [{ urls: `stun:${location.hostname}:3478` }];
  }
}

/** Дождаться сбора ICE-кандидатов (или потолка ожидания). */
function waitForIce(pc) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', check);
      clearTimeout(timer);
      resolve();
    };
    const check = () => { if (pc.iceGatheringState === 'complete') done(); };
    const timer = setTimeout(done, GATHER_TIMEOUT);
    pc.addEventListener('icegatheringstatechange', check);
  });
}

/**
 * Открыть WebRTC-соединение.
 *
 * `on` — три колбэка: open() / data(ArrayBuffer) / close(). Ошибки установки
 * приезжают тем же close(), а не исключением: для вызывающего «не поднялось»
 * и «оборвалось» — одно и то же событие, и обрабатывать их врозь незачем.
 */
export function openRtc(on) {
  if (typeof RTCPeerConnection === 'undefined') {
    // Браузер без WebRTC. Сообщаем закрытием, чтобы вызывающий показал
    // «недоступен» и оставил игроку WebSocket.
    setTimeout(() => on.close(), 0);
    return { send() {}, close() {} };
  }

  let pc = null;
  let dc = null;
  let dead = false;

  // Закрытие обязано быть идемпотентным: сигнал прилетает и от канала,
  // и от состояния соединения, и от нашего собственного close().
  const die = () => {
    if (dead) return;
    dead = true;
    try { pc && pc.close(); } catch { /* уже закрыт */ }
    on.close();
  };

  (async () => {
    try {
      pc = new RTCPeerConnection({ iceServers: await iceServers() });
      if (dead) { pc.close(); return; }

      // ⚠️ Ключевые параметры канала. ordered:false — кадры игре нужны свежие,
      // а не по порядку; maxRetransmits:0 — потерянный снапшот переигрывать
      // бессмысленно, следующий приедет через 33 мс. Именно эти два флага
      // и отличают WebRTC от WebSocket по ощущению на плохом канале;
      // без них мы бы получили тот же TCP, только сложнее.
      dc = pc.createDataChannel('game', { ordered: false, maxRetransmits: 0 });
      dc.binaryType = 'arraybuffer';
      dc.onopen = () => { if (!dead) on.open(); };
      dc.onmessage = (e) => { if (!dead) on.data(e.data); };
      dc.onclose = die;
      dc.onerror = die;

      pc.onconnectionstatechange = () => {
        if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) die();
      };

      await pc.setLocalDescription(await pc.createOffer());
      await waitForIce(pc);
      if (dead) return;

      // Сигналинг по HTTP, а НЕ по нашему же WebSocket — намеренно: второй
      // транспорт должен подниматься даже тогда, когда первый сломан, иначе
      // он бесполезен как запасной путь.
      const r = await fetch(`${apiBase()}/api/rtc/offer`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ sdp: pc.localDescription.sdp }),
      });
      if (!r.ok) throw new Error(`сигналинг ответил ${r.status}`);
      const { sdp } = await r.json();
      if (dead) return;
      await pc.setRemoteDescription({ type: 'answer', sdp });
    } catch (e) {
      console.warn('WebRTC не поднялся:', e.message || e);
      die();
    }
  })();

  return {
    send(buf) {
      // readyState проверяем каждый раз: канал может закрыться между кадрами,
      // а send() по закрытому каналу бросает исключение прямо в игровой цикл.
      if (dc && dc.readyState === 'open') dc.send(buf);
    },
    close: die,
  };
}
